import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { NumericField } from '../../components/NumericField';
import { getNumericFieldError } from '../../utils/numericValidation';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { TabsContent } from '../../components/ui/tabs';
import { Plus, Trash2, Edit2, Eye, Loader2 } from 'lucide-react';

const COMMON_GLASS_THICKNESSES = [
  { value: 0.125, label: '1/8" (3mm)' },
  { value: 0.1875, label: '3/16" (5mm)' },
  { value: 0.25, label: '1/4" (6mm)' },
  { value: 0.3125, label: '5/16" (8mm)' },
  { value: 0.375, label: '3/8" (10mm)' },
  { value: 0.5, label: '1/2" (12mm)' },
  { value: 0.625, label: '5/8" (16mm)' },
  { value: 0.75, label: '3/4" (19mm)' },
  { value: 1.0, label: '1" (25mm)' },
];

const glassTypeSpecs = [
  { id: 'tempered', name: 'Tempered' },
  { id: 'laminated', name: 'Laminated' },
  { id: 'insulated', name: 'Insulated' },
  { id: 'low-e', name: 'Low-E' },
  { id: 'tinted', name: 'Tinted' },
  { id: 'reflective', name: 'Reflective' },
  { id: 'spandrel', name: 'Spandrel' },
  { id: 'frosted', name: 'Frosted' },
];

export const SFGlassTypesSection = ({
  // Data
  storefrontGlassTypes,
  selectedSFGlassTypeIds,
  expandedSFGlassTypeIds,
  newSFGlassType,
  editingSFGlassType,
  showSFGlassTypeDialog,
  operationLoading,
  // Permissions
  canManageProject,
  // Refs
  sfGridSizeRef,
  // Handlers
  setNewSFGlassType,
  setShowSFGlassTypeDialog,
  setEditingSFGlassType,
  setExpandedSFGlassTypeIds,
  selectAllItems,
  confirmBulkDelete,
  toggleItemSelection,
  handleCreateSFGlassType,
  handleEditSFGlassType,
  handleDeleteSFGlassType,
}) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const numericErrors = [
    getNumericFieldError(newSFGlassType.warrantyYears, { integer: true, gtZero: true, label: 'Warranty' }),
    newSFGlassType.hasGrid
      ? getNumericFieldError(newSFGlassType.gridSize_in, { required: true, min: 0, allowFraction: true, label: 'Grid size' })
      : null,
  ];
  const handleSubmit = (e) => {
    setSubmitAttempted(true);
    if (numericErrors.some(Boolean)) {
      e.preventDefault();
      return;
    }
    handleCreateSFGlassType(e);
  };

  return (
    <TabsContent value="sf-glass-types">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Glass Types</h2>
        <div className="flex gap-2">
          {canManageProject && storefrontGlassTypes.length > 0 && (
            <>
              <Button variant="outline" onClick={() => selectAllItems('sfGlassType')}>
                {selectedSFGlassTypeIds.length === storefrontGlassTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={() => confirmBulkDelete('sfGlassType')}
                disabled={selectedSFGlassTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedSFGlassTypeIds.length})
              </Button>
            </>
          )}
          {canManageProject && (
            <Dialog open={showSFGlassTypeDialog} onOpenChange={(open) => {
              setSubmitAttempted(false);
              setShowSFGlassTypeDialog(open);
              if (!open) {
                setEditingSFGlassType(null);
                setNewSFGlassType({
                  name: '',
                  manufacturer: '',
                  model: '',
                  thickness_in: null,
                  colorCode: '',
                  specifications: [],
                  warrantyYears: ''
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Glass Type
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingSFGlassType ? 'Edit Glass Type' : 'Add Glass Type'}</DialogTitle>
                <DialogDescription>Define glass specifications for storefront</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type (e.g., GL1, GL2) *</Label>
                    <Input value={newSFGlassType.name} onChange={(e) => setNewSFGlassType({...newSFGlassType, name: e.target.value})} placeholder="e.g., GL1, GL2" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Manufacturer *</Label>
                    <Input value={newSFGlassType.manufacturer} onChange={(e) => setNewSFGlassType({...newSFGlassType, manufacturer: e.target.value})} placeholder="e.g., Guardian, PPG" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Model *</Label>
                    <Input value={newSFGlassType.model} onChange={(e) => setNewSFGlassType({...newSFGlassType, model: e.target.value})} placeholder="e.g., SunGuard" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Thickness (in) *</Label>
                    <Select 
                      value={newSFGlassType.thickness_in?.toString() || ''} 
                      onValueChange={(val) => setNewSFGlassType({...newSFGlassType, thickness_in: parseFloat(val)})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select thickness" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMMON_GLASS_THICKNESSES.map((t) => (
                          <SelectItem key={t.value} value={t.value.toString()}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Color Code (HEX/RAL)</Label>
                    <Input value={newSFGlassType.colorCode} onChange={(e) => setNewSFGlassType({...newSFGlassType, colorCode: e.target.value})} placeholder="#FFFFFF or RAL 9016" />
                  </div>
                  <div className="space-y-2">
                    <Label>Build</Label>
                    <Input 
                      value={newSFGlassType.build || ''} 
                      onChange={(e) => setNewSFGlassType({...newSFGlassType, build: e.target.value})} 
                      placeholder="e.g., 1/4 + 1/2 Air + 1/4" 
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Specifications</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 border rounded-md">
                    {glassTypeSpecs.map((spec) => (
                      <label
                        key={spec.id}
                        htmlFor={`sf-spec-${spec.id}`}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <Checkbox
                          id={`sf-spec-${spec.id}`}
                          checked={newSFGlassType.specifications.includes(spec.name)}
                          onCheckedChange={(checked) => {
                            const updated = checked
                              ? [...newSFGlassType.specifications, spec.name]
                              : newSFGlassType.specifications.filter(s => s !== spec.name);
                            setNewSFGlassType({...newSFGlassType, specifications: updated});
                          }}
                        />
                        <span className="text-sm">{spec.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Warranty Period (Years)</Label>
                  <NumericField
                    placeholder="e.g., 10"
                    value={newSFGlassType.warrantyYears}
                    onChange={(v) => setNewSFGlassType({ ...newSFGlassType, warrantyYears: v })}
                    rules={{ integer: true, gtZero: true, label: 'Warranty' }}
                    showError={submitAttempted}
                    data-testid="sf-glass-warranty-input"
                    errorTestId="sf-glass-warranty-error"
                  />
                </div>
                
                <div className="space-y-2 border-t pt-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={newSFGlassType.hasGrid || false}
                      onCheckedChange={(isChecked) => {
                        setNewSFGlassType({
                          ...newSFGlassType, 
                          hasGrid: isChecked,
                          gridSize_in: isChecked ? newSFGlassType.gridSize_in : null
                        });
                        if (isChecked) {
                          setTimeout(() => sfGridSizeRef.current?.focus(), 100);
                        }
                      }}
                    />
                    <span>This glass type has grid</span>
                  </label>
                </div>
                
                {newSFGlassType.hasGrid && (
                  <div className="space-y-2">
                    <Label>Grid Size (in) *</Label>
                    <NumericField
                      value={newSFGlassType.gridSize_in}
                      onChange={(v) => setNewSFGlassType({ ...newSFGlassType, gridSize_in: v })}
                      rules={{ required: true, min: 0, allowFraction: true, label: 'Grid size' }}
                      showError={submitAttempted}
                      placeholder="e.g., 6"
                      required
                      data-testid="sf-glass-grid-size-input"
                      errorTestId="sf-glass-grid-size-error"
                    />
                  </div>
                )}
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowSFGlassTypeDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={operationLoading}>
                    {operationLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {editingSFGlassType ? 'Updating...' : 'Creating...'}
                      </>
                    ) : (
                      <>{editingSFGlassType ? 'Update' : 'Create'} Glass Type</>
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {storefrontGlassTypes.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg">
          <p>No glass types created yet</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canManageProject && (
                  <th className="w-12 px-3 py-3 text-left">
                    <Checkbox
                      checked={selectedSFGlassTypeIds.length === storefrontGlassTypes.length && storefrontGlassTypes.length > 0}
                      onCheckedChange={() => selectAllItems('sfGlassType')}
                    />
                  </th>
                )}
                <th className="w-24 px-3 py-3 text-left font-semibold text-slate-700">Type</th>
                <th className="w-36 px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="w-36 px-3 py-3 text-left font-semibold text-slate-700">Model</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Build</th>
                {canManageProject && (
                  <th className="w-28 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {storefrontGlassTypes.map((glass, idx) => (
                <React.Fragment key={glass.id}>
                  <tr 
                    className={`border-b hover:bg-slate-50 transition-colors ${
                      selectedSFGlassTypeIds.includes(glass.id) ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    }`}
                  >
                    {canManageProject && (
                      <td className="w-12 px-3 py-2">
                        <Checkbox
                          checked={selectedSFGlassTypeIds.includes(glass.id)}
                          onCheckedChange={(checked) => toggleItemSelection(glass.id, 'sfGlassType', { target: { checked } })}
                        />
                      </td>
                    )}
                    <td className="w-24 px-3 py-2 font-medium text-slate-900">{glass.name}</td>
                    <td className="w-36 px-3 py-2 text-slate-600 truncate">{glass.manufacturer || '-'}</td>
                    <td className="w-36 px-3 py-2 text-slate-600 truncate">{glass.model || '-'}</td>
                    <td className="px-3 py-2 text-slate-600 truncate">{glass.build || '-'}</td>
                    {canManageProject && (
                      <td className="w-28 px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className={`h-7 w-7 p-0 transition-colors ${
                              expandedSFGlassTypeIds.includes(glass.id) 
                                ? 'bg-orange-500 text-white hover:bg-orange-600' 
                                : 'text-slate-500 hover:bg-orange-500 hover:text-white'
                            }`}
                            onClick={() => setExpandedSFGlassTypeIds(prev => 
                              prev.includes(glass.id) 
                                ? prev.filter(id => id !== glass.id) 
                                : [...prev, glass.id]
                            )}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditSFGlassType(glass)}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white" onClick={() => handleDeleteSFGlassType(glass.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {expandedSFGlassTypeIds.includes(glass.id) && (
                    <tr className="bg-slate-50 border-b">
                      {canManageProject && <td className="w-12"></td>}
                      <td colSpan={canManageProject ? 5 : 4} className="px-3 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                          <div>
                            <span className="font-semibold text-slate-700">Thickness:</span>
                            <span className="ml-2 text-slate-600">{glass.thickness_in ? `${glass.thickness_in}"` : '-'}</span>
                          </div>
                          <div>
                            <span className="font-semibold text-slate-700">Color Code:</span>
                            <span className="ml-2 text-slate-600">{glass.colorCode || '-'}</span>
                          </div>
                          {glass.hasGrid && (
                            <div>
                              <span className="font-semibold text-slate-700">Grid Size:</span>
                              <span className="ml-2 text-slate-600">{glass.gridSize_in ? `${glass.gridSize_in}"` : 'Yes'}</span>
                            </div>
                          )}
                          {glass.warrantyYears && (
                            <div>
                              <span className="font-semibold text-slate-700">Warranty:</span>
                              <span className="ml-2 text-slate-600">{glass.warrantyYears} Years</span>
                            </div>
                          )}
                          {glass.specifications && glass.specifications.length > 0 && (
                            <div className="col-span-2 md:col-span-4 mt-1">
                              <span className="font-semibold text-slate-700">Specifications:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {glass.specifications.map((spec, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-slate-200 rounded text-xs">{spec}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TabsContent>
  );
};

export default SFGlassTypesSection;
